import { api } from 'src/boot/axios';
import { defineStore } from 'pinia';
import { getResponseData } from 'utils';
import { TNormalizedUnit, TUnitDataDictionaryEntry } from 'types';
import { constants } from 'boot/constants';

const UNITS_DATA_DICTIONARY_KEY = 'units-data-dictionary';

type State = {
  units: TNormalizedUnit[];
  dataDictionary: TUnitDataDictionaryEntry[];
  pagination: {
    sortBy: string;
    page: number;
    perPage: number;
    total: number;
    totalPages: number;
    lastPage: number;
    search: string;
    organisationId: number | null;
    regionId: number | null;
    groupId: number | null;
  };
  loading: boolean;
};

export const useUnitsStore = defineStore('units', {
  state: (): State => ({
    units: [],
    dataDictionary: [],
    pagination: {
      sortBy: 'org_display_seq',
      page: 1,
      perPage: 12,
      total: 0,
      totalPages: 0,
      lastPage: 1,
      search: '',
      organisationId: null,
      regionId: null,
      groupId: null,
    },
    loading: false,
  }),
  getters: {
    unitsByRegion:
      (state) =>
      (regionId: number): TUnitDataDictionaryEntry[] => {
        return state.dataDictionary
          .filter((unit) => unit.region_id === regionId)
          .sort((a, b) => a.unit_name.localeCompare(b.unit_name));
      },
    unitsByName:
      (state) =>
      (search: string, regionId?: number): TUnitDataDictionaryEntry[] => {
        const needle = search.toLowerCase();
        return state.dataDictionary
          .filter((unit) => {
            const matchesName = unit.unit_name.toLowerCase().includes(needle);
            const matchesRegion =
              regionId === undefined || unit.region_id === regionId;
            return matchesName && matchesRegion;
          })
          .sort((a, b) => a.unit_name.localeCompare(b.unit_name));
      },
  },
  actions: {
    async getUnit(id: number): Promise<TNormalizedUnit> {
      const response = await api.get(`/api/v1/units/${id}`);
      return getResponseData(response) as TNormalizedUnit;
    },
    async getUnits() {
      this.loading = true;

      const params = new URLSearchParams();

      params.append('pg', this?.pagination.page?.toString() ?? '1');
      params.append('ps', this?.pagination.perPage?.toString() ?? '10');
      params.append('sort', this.pagination.sortBy);

      if (this.pagination.search) {
        params.append('filter[search]', this.pagination.search);
      }

      if (this.pagination.organisationId) {
        params.append(
          'filter[organisation]',
          this.pagination.organisationId?.toString()
        );
      }

      if (this.pagination.regionId) {
        params.append('filter[region]', this.pagination.regionId?.toString());
      }

      if (this.pagination.groupId) {
        params.append('filter[group]', this.pagination.groupId?.toString());
      }

      const urlParams = decodeURIComponent(params.toString());

      try {
        const response = await api.get(`/api/v1/units?${urlParams}`);

        const data = getResponseData(response) as TNormalizedUnit[];

        const paginationData = response.data.meta.page;

        this.units = data;
        this.pagination = {
          ...this.pagination,
          page: paginationData.current_page,
          perPage: paginationData.per_page,
          total: paginationData.total,
          totalPages: paginationData.total_pages,
          lastPage: paginationData.total_pages,
        };
      } catch {
        // Handle permission errors (403) and other failures gracefully
        this.units = [];
        this.pagination = {
          ...this.pagination,
          page: 1,
          total: 0,
          totalPages: 0,
          lastPage: 1,
        };
      } finally {
        this.loading = false;
      }
    },
    clearUnits() {
      this.units = [];
    },
    clear() {
      this.units = [];
      this.pagination = {
        sortBy: 'org_display_seq',
        page: 1,
        perPage: 12,
        total: 0,
        totalPages: 0,
        lastPage: 1,
        search: '',
        organisationId: null,
        regionId: null,
        groupId: null,
      };
      this.loading = false;
    },
    clearDataDictionary() {
      this.dataDictionary = [];
    },
    async getUnitsForQuery(query: string): Promise<TNormalizedUnit[]> {
      const response = await api.get(
        '/api/v1/registration-units?' +
          `filter[unit_status_id]=${[
            constants.LODGE_STATUS_NORMAL,
            constants.CHAPTER_STATUS_NORMAL,
            constants.LODGE_STATUS_NOT_MEETING,
            constants.CHAPTER_STATUS_NOT_MEETING,
          ].join(',')}&` +
          `filter[unit_name]=${query}`
      );

      return getResponseData(response.data) as TNormalizedUnit[];
    },

    // Data dictionary methods
    cacheDataDictionary(data: TUnitDataDictionaryEntry[]) {
      if (data) {
        localStorage.setItem(UNITS_DATA_DICTIONARY_KEY, JSON.stringify(data));
      }
    },

    fetchDataDictionary() {
      return api.get('/api/v1/units/data-dictionary');
    },

    async getDataDictionary() {
      // Load from cache first
      const cachedData = localStorage.getItem(UNITS_DATA_DICTIONARY_KEY);
      if (cachedData) {
        this.dataDictionary = JSON.parse(
          cachedData
        ) as TUnitDataDictionaryEntry[];

        // Fetch fresh data in background
        void this.fetchDataDictionary().then((response) => {
          const data = response.data?.data as TUnitDataDictionaryEntry[];
          this.dataDictionary = data;
          this.cacheDataDictionary(data);
        });
        return;
      }

      // No cache - fetch and cache
      const response = await this.fetchDataDictionary();
      const data = response.data?.data as TUnitDataDictionaryEntry[];
      this.dataDictionary = data;
      this.cacheDataDictionary(data);
    },
  },
});
